import { Box, Button as MuiButton, TextField, Typography } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs/ClosableDialog'
import { Button } from 'components/facets'
import { submitBugReport } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Node forwards the ticket to Rails with axios, which refuses bodies over 10 MB
const MAX_ATTACHMENTS_BYTES = 10 * 1024 * 1024
const COMPRESS_IMAGES_ABOVE_BYTES = 1024 * 1024
const MAX_IMAGE_SIDE = 2000

// Screenshots from high-DPI screens weigh several MB: shrink and re-encode them as JPEG,
// keeping the original whenever the browser can't decode it or the result isn't smaller
const compressImage = async (file: File): Promise<File> => {
  if (
    !file.type.startsWith('image/') ||
    file.type === 'image/gif' ||
    file.size <= COMPRESS_IMAGES_ABOVE_BYTES
  ) {
    return file
  }
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const context = canvas.getContext('2d')!
    // JPEG has no transparency: paint white first, or transparent areas turn black
    context.fillStyle = '#fff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    )
    if (!blob || blob.size >= file.size) return file
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
  } catch {
    return file
  }
}

interface BugReportDialogProps {
  open: boolean
  onClose: () => void
  // Subject chosen by us: the field is hidden and the ticket is sent with this one
  fixedSubject?: string
  hideAttachments?: boolean
  // Prefills the description, for a request the user would otherwise have to write themselves
  defaultDescription?: string
}

export const BugReportDialog = ({
  open,
  onClose,
  fixedSubject,
  hideAttachments,
  defaultDescription = '',
}: BugReportDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [subject, setSubject] = useState(fixedSubject ?? '')
  const [description, setDescription] = useState(defaultDescription)
  useEffect(() => {
    if (open) {
      setSubject(fixedSubject ?? '')
      setDescription(defaultDescription)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const [attachments, setAttachments] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [subjectError, setSubjectError] = useState(false)
  const [descriptionError, setDescriptionError] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback((files: FileList | File[]) => {
    setAttachments((prev) => [...prev, ...Array.from(files)])
  }, [])

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }

  const handleClose = () => {
    setSubject('')
    setDescription('')
    setAttachments([])
    setSubjectError(false)
    setDescriptionError(false)
    onClose()
  }

  const handleSubmit = async () => {
    const hasSubjectError = !subject.trim()
    const hasDescriptionError = !description.trim()
    setSubjectError(hasSubjectError)
    setDescriptionError(hasDescriptionError)

    if (hasSubjectError || hasDescriptionError) return

    setLoading(true)
    try {
      const files = await Promise.all(attachments.map(compressImage))
      if (files.reduce((total, file) => total + file.size, 0) > MAX_ATTACHMENTS_BYTES) {
        enqueueSnackbar(t('bugReport.attachmentsTooLarge'), { variant: 'error' })
        return
      }
      await submitBugReport(subject, description, files.length > 0 ? files : undefined)
      enqueueSnackbar(t('bugReport.success'), { variant: 'success' })
      handleClose()
    } catch {
      enqueueSnackbar(t('bugReport.error'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ClosableDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <h3>{t('bugReport.title')}</h3>
      {!fixedSubject && (
        <Box mt={2}>
          <TextField
            fullWidth
            label={t('bugReport.subject')}
            placeholder={t('bugReport.subjectPlaceholder') as string}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value)
              if (subjectError) setSubjectError(false)
            }}
            error={subjectError}
            helperText={subjectError ? t('bugReport.subjectRequired') : ''}
            variant="outlined"
          />
        </Box>
      )}
      <Box mt={2}>
        <TextField
          fullWidth
          multiline
          variant="outlined"
          rows={5}
          label={t('bugReport.description')}
          placeholder={t('bugReport.descriptionPlaceholder') as string}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
            if (descriptionError) setDescriptionError(false)
          }}
          error={descriptionError}
          helperText={descriptionError ? t('bugReport.descriptionRequired') : ''}
        />
      </Box>
      {!hideAttachments && (
        <Box mt={2}>
          <Typography variant="body2">{t('bugReport.attachments')}</Typography>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? '#1976d2' : '#ccc'}`,
              borderRadius: 8,
              padding: '20px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              transition: 'border-color 0.2s, background 0.2s',
              marginTop: 8,
            }}
          >
            <i className="fa-light fa-cloud-arrow-up" style={{ fontSize: 24, color: '#888' }} />
            <Typography variant="body2" style={{ color: '#888', marginTop: 4 }}>
              {t('bugReport.dropzone')}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files)
                e.target.value = ''
              }}
            />
          </Box>
          {attachments.length > 0 && (
            <Box mt={1}>
              {attachments.map((file, i) => (
                <Box
                  key={`${file.name}-${i}`}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ padding: '2px 0' }}
                >
                  <Typography variant="caption">{file.name}</Typography>
                  <MuiButton
                    size="small"
                    onClick={() => removeFile(i)}
                    style={{ minWidth: 'auto' }}
                  >
                    <i className="fa-solid fa-xmark" />
                  </MuiButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Box mt={3} display="flex" justifyContent="flex-end" gridGap={8}>
        <Button variant="outlined" color="primary" onClick={handleClose} disabled={loading}>
          {t('bugReport.cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {t('bugReport.submit')}
        </Button>
      </Box>
    </ClosableDialog>
  )
}
